import { ListOrdered } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { SectionCard } from "../dashboard/DashboardPrimitives";
import { JOB_STATUS_VARIANT } from "./badges";
import { formatAge, formatDuration, planeLabel } from "./fleetSummary";
import { echoPath, failureLabel, windowLabel } from "./fleetWindow";
import type { H3History, H3Job } from "./h3Fleet";

function ClipRow({ clip, now }: { clip: H3Job; now: Date }) {
  const endedAt = Date.parse(clip.ended_at ?? clip.last_event_at);
  // The composer numbers scenes (`scene_id: 3`); older APIs pass the number through.
  const shortScene =
    clip.scene_id !== null && clip.scene_id !== undefined
      ? String(clip.scene_id).slice(0, 8)
      : null;
  return (
    <TableRow>
      <TableCell
        className="text-xs"
        title={clip.ended_at ?? clip.last_event_at}
      >
        {formatAge((now.getTime() - endedAt) / 1000)}
      </TableCell>
      <TableCell className="font-mono text-xs">
        {clip.workload_id ? (
          <Link
            to={echoPath(clip.workload_id)}
            className="underline underline-offset-4"
            title={`Open Echo ${clip.workload_id}`}
          >
            {clip.workload_id.slice(0, 8)}
          </Link>
        ) : (
          "—"
        )}
        <span className="block text-muted-foreground" title={clip.job_id}>
          {shortScene ? `scene ${shortScene} · ` : ""}clip{" "}
          {clip.job_id.slice(0, 8)}
        </span>
      </TableCell>
      <TableCell>
        <Badge variant={JOB_STATUS_VARIANT[clip.status]}>
          {clip.status === "completed" ? "delivered" : clip.status}
        </Badge>
        {clip.status === "failed" ? (
          <span className="block pt-1 text-xs text-muted-foreground">
            {failureLabel(clip.last_reason)}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="text-xs">
        {planeLabel(clip.plane)}
        {clip.worker_id ? (
          <span className="block font-mono text-muted-foreground">
            {clip.worker_id}
          </span>
        ) : null}
        {clip.gpu ? (
          <span className="block text-muted-foreground">{clip.gpu}</span>
        ) : null}
      </TableCell>
      <TableCell className="text-xs tabular-nums">
        {formatDuration(clip.gen_seconds)}
      </TableCell>
      <TableCell className="text-xs tabular-nums">
        {clip.attempt ?? 1}
        {clip.redispatched ? ` · re-posted ${clip.redispatched}×` : ""}
        {clip.lost ? ` · lost ${clip.lost}×` : ""}
      </TableCell>
    </TableRow>
  );
}

/** The clips that finished in the window, newest first, each linking to its Echo. */
export function ClipsTable({
  history,
  now,
}: {
  history: H3History;
  now: Date;
}) {
  const clips = history.clips ?? [];
  const total = history.total ?? clips.length;
  const label = windowLabel(history.window_hours);
  return (
    <SectionCard
      title={`Clips, last ${label}`}
      icon={<ListOrdered className="size-4" />}
      action={
        <span className="text-xs text-muted-foreground">
          {total} finished
          {clips.length < total ? ` · newest ${clips.length}` : ""}
        </span>
      }
    >
      {clips.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No H3 clip finished in this environment in the last {label}.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Finished</TableHead>
              <TableHead>Echo · scene</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Plane · worker</TableHead>
              <TableHead>Gen</TableHead>
              <TableHead>Attempts</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clips.map((clip) => (
              <ClipRow key={clip.job_id} clip={clip} now={now} />
            ))}
          </TableBody>
        </Table>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Open the Echo to watch the delivered film and its scenes. The
        clip&apos;s own frames stay in the H3 work bucket, which the CRM cannot
        read.
      </p>
    </SectionCard>
  );
}
